/**
 * Planner Workspace (/planner) — the studio screen.
 *
 * Three views, in the order a planner actually asks the questions:
 *   Season    — a calendar carrying THREE layers: the event days, the RSVP
 *               deadlines (the date that really drives the week, and which
 *               nothing in the product surfaced before), and the planner's own
 *               diary — tastings, site visits, days they are not available.
 *   Clients   — the same events grouped by whose they are, because one client
 *               can hold several events and a flat form list cannot say that.
 *   Needs you — what is closing soonest with replies still missing.
 *
 * WHAT THIS FILE DOES NOT OWN
 * ---------------------------
 * The My Branding, Templates and Billing dialogs are my-projects.js's, loaded
 * alongside this page. Reimplementing them here would have produced a second
 * copy of ~200 lines that drifts from the first the day either changes.
 *
 * DATA
 * ----
 *   GET  /api/planner/workspace        events + clients + diary, one call
 *   POST /api/forms/portfolio-stats    the richer per-event numbers, reused
 *   POST/DELETE /api/planner/notes     the diary
 *   PUT  /api/planner/clients/:formId  file an event under a client
 */
(function () {
  'use strict';

  var state = {
    events: [],
    notes: [],
    tasks: [],            // task #696
    alerts: { windows: [30, 14, 3], emails: false },
    clients: [],
    profile: {},
    month: null,          // Date, first of the displayed month
    view: 'season',
    isPlanner: false,
    loaded: false
  };

  var $ = function (id) { return document.getElementById(id); };

  function esc(s) {
    var d = document.createElement('div');
    d.textContent = String(s == null ? '' : s);
    return d.innerHTML;
  }

  function toast(msg, kind) {
    if (window.myProjects && window.myProjects.showToast) {
      window.myProjects.showToast(msg, kind || 'info');
    } else {
      console.info(msg);
    }
  }

  /* =======================================================================
     Auth + fetch
     ======================================================================= */

  function authHeaders() {
    var u = window.firebaseAuth && window.firebaseAuth.currentUser;
    if (!u) return Promise.resolve({ 'Content-Type': 'application/json' });
    return u.getIdToken().then(function (t) {
      return { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + t };
    });
  }

  function api(path, options) {
    return authHeaders().then(function (headers) {
      return fetch(path, Object.assign({ headers: headers }, options || {}));
    }).then(function (res) {
      return res.json().catch(function () { return {}; }).then(function (data) {
        if (!res.ok || data.success === false) {
          throw new Error(data.error || data.message || 'Something went wrong.');
        }
        return data;
      });
    });
  }

  /* =======================================================================
     Client colour.

     Derived from the client's NAME, so the same couple is the same colour on
     every screen and across reloads without anything being stored. Hue only —
     saturation and lightness are fixed, which is what stops eight clients
     turning the calendar into a bag of sweets.
     ======================================================================= */

  function clientHue(name) {
    var s = String(name || '');
    var h = 0;
    for (var i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) % 360;
    return h;
  }

  function clientColor(name, kind) {
    if (!name) return kind === 'soft' ? '#e9ecef' : '#8a94a6';
    var h = clientHue(name);
    return kind === 'soft' ? 'hsl(' + h + ' 62% 94%)' : 'hsl(' + h + ' 46% 42%)';
  }

  /* =======================================================================
     Dates
     ======================================================================= */

  var MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];

  function ymd(d) {
    return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0')
      + '-' + String(d.getDate()).padStart(2, '0');
  }

  function monthLabel(d) {
    try {
      return new Intl.DateTimeFormat(document.documentElement.lang || 'en',
        { month: 'long', year: 'numeric' }).format(d);
    } catch (e) {
      return MONTHS[d.getMonth()] + ' ' + d.getFullYear();
    }
  }

  /** "in 5 days" / "today" / "12 days ago" — the phrasing a planner thinks in. */
  function relDays(n) {
    if (n == null) return '';
    if (n === 0) return 'today';
    if (n === 1) return 'tomorrow';
    if (n === -1) return 'yesterday';
    return n > 0 ? 'in ' + n + ' days' : Math.abs(n) + ' days ago';
  }

  /* =======================================================================
     Load
     ======================================================================= */

  function load() {
    return api('/api/planner/workspace').then(function (data) {
      state.events = data.events || [];
      state.notes = data.notes || [];
      // Task #696: the planner's own tasks and alert timing.
      state.tasks = data.tasks || [];
      state.alerts = data.alerts || { windows: [30, 14, 3], emails: false };
      state.clients = data.clients || [];
      state.loaded = true;

      // The richer per-event numbers come from the endpoint that already
      // computes them for My Events. A failure here costs the response bars
      // and nothing else, so it never blocks the page.
      var ids = state.events.map(function (e) { return e.id; });
      if (!ids.length) return;
      return api('/api/forms/portfolio-stats', {
        method: 'POST', body: JSON.stringify({ formIds: ids })
      }).then(function (stats) {
        state.events.forEach(function (e) {
          var s = (stats.stats || {})[e.id];
          if (!s) return;
          e.attending = s.attending;
          e.guests = s.guests;
          e.invited = s.invited || null;
          e.responses = typeof s.responses === 'number' ? s.responses : e.responses;
          if (e.invited > 0) {
            e.responseRate = Math.min(1, e.responses / e.invited);
            // Re-score now that we know how many are still missing. The server
            // scored on the deadline alone, treating the gap as half-unknown.
            e.attention = rescore(e);
          }
        });
      }).catch(function () { /* bars off, page fine */ });
    });
  }

  /** Mirror of attentionScore() in planner-workspace.service.js. */
  function rescore(e) {
    var horizon = e.deadlineInDays == null ? e.eventInDays : e.deadlineInDays;
    if (horizon == null || horizon < 0) return 0;
    var pressure = Math.max(0, Math.min(1, (45 - horizon) / 45));
    var gap = e.responseRate == null ? 0.5 : Math.max(0, 1 - e.responseRate);
    var penalty = e.deadlineInDays == null ? 0.6 : 1;
    return Math.round(pressure * gap * penalty * 100);
  }

  /* =======================================================================
     Studio header
     ======================================================================= */

  /**
   * Paint the studio header from a branding profile. Split out from the fetch
   * so a SAVE can repaint it with what it just saved (task #647) — this header
   * was drawn once on load and had no idea a new logo had been uploaded, so it
   * kept the old one until a reload.
   */
  function paintStudioBranding(p) {
    p = p || {};
    state.profile = p;
    var name = (p.companyName || '').trim();
    if (name) {
      $('pwStudioName').textContent = name;
      $('pwStudioEyebrow').innerHTML = '<i class="bi bi-stars"></i>Event Planner';
    } else {
      // No branding set yet: say what the field is for rather than leaving a
      // blank heading. The Branding section is the fix, so link to it.
      $('pwStudioName').textContent = 'Your studio';
      $('pwStudioEyebrow').innerHTML = '<i class="bi bi-pencil"></i>Add your name and logo';
    }
    var mark = $('pwStudioMark');
    if (p.logoUrl) {
      mark.classList.add('has-logo');
      mark.innerHTML = '<img src="' + esc(p.logoUrl) + '" alt="">';
      mark.querySelector('img').addEventListener('error', function () {
        mark.classList.remove('has-logo');
        mark.textContent = initials(name);
      });
    } else {
      mark.classList.remove('has-logo');
      mark.textContent = initials(name);
    }
  }

  // Saving branding repaints the header immediately, using the profile the save
  // returned — no second request, and nothing served out of the cache we asked
  // the browser to keep for the guest-facing copy of the same data.
  document.addEventListener('quikrsvp:planner-branding-saved', function (e) {
    if (!$('pwStudioName')) return;          // not on the studio page
    paintStudioBranding((e.detail && e.detail.profile) || {});
  });

  function renderStudio() {
    api('/api/planner/branding').then(function (data) {
      paintStudioBranding(data.profile || {});
    }).catch(function () { $('pwStudioName').textContent = 'Your studio'; });

    wireSidebar();
    // The sidebar no longer carries a running total (task #650), so the
    // studio view no longer fetches the ledger just to fill it. Billing
    // loads its own months when a planner opens it.
  }

  /**
   * Collapse, the same way the org dashboard does it, down to the body class
   * its stylesheet already keys on — so the rail narrows to icons and the
   * choice survives a reload.
   */
  function wireSidebar() {
    var btn = $('pwSidebarToggle');
    if (!btn || btn.dataset.wired) return;
    btn.dataset.wired = '1';
    var KEY = 'pwSidebarCollapsed';
    function paint(on) {
      document.body.classList.toggle('org-sidebar-collapsed', on);
      btn.title = on ? 'Expand sidebar' : 'Collapse sidebar';
      btn.querySelector('i').className = on ? 'bi bi-chevron-right' : 'bi bi-chevron-left';
    }
    try { paint(localStorage.getItem(KEY) === '1'); } catch (e) { /* private mode */ }
    btn.addEventListener('click', function () {
      var on = !document.body.classList.contains('org-sidebar-collapsed');
      paint(on);
      try { localStorage.setItem(KEY, on ? '1' : '0'); } catch (e) { /* private mode */ }
    });
  }

  function initials(name) {
    var parts = String(name || 'Studio').trim().split(/\s+/).slice(0, 2);
    return parts.map(function (w) { return w[0] || ''; }).join('').toUpperCase() || 'S';
  }

  // monthLabelFromKey lived here to caption the sidebar's month tile. The tile
  // is gone (task #650) and it had no other caller, so it went with it —
  // PlannerModals.monthLabel is still the one implementation, used by Billing.

  function money(amount, currency) {
    var n = Number(amount) || 0;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency', currency: String(currency || 'usd').toUpperCase()
      }).format(n);
    } catch (e) {
      return '$' + n.toFixed(2);
    }
  }

  /* =======================================================================
     Season — the calendar
     ======================================================================= */

  function renderSeason() {
    var host = $('pwSeason');
    var month = state.month;
    var first = new Date(month.getFullYear(), month.getMonth(), 1);
    var startDay = first.getDay();
    var daysInMonth = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
    var today = ymd(new Date());

    // Everything that lands on a day, keyed by date.
    var byDate = {};
    function put(date, chip) {
      if (!date) return;
      (byDate[date] = byDate[date] || []).push(chip);
    }
    state.events.forEach(function (e) {
      put(e.eventDate, { type: 'event', label: e.title, client: e.client, id: e.id });
      if (e.rsvpDeadline && e.rsvpDeadline !== e.eventDate) {
        put(e.rsvpDeadline, { type: 'deadline', label: 'RSVP by — ' + e.title, client: e.client, id: e.id });
      }
    });
    state.notes.forEach(function (n) {
      put(n.date, { type: 'note', label: n.title, kind: n.kind, id: n.id });
    });
    // Task #696: dated tasks, done ones greyed in place rather than removed -
    // a planner looking back at last week wants to see what they finished.
    (state.tasks || []).forEach(function (t) {
      if (t.due) put(t.due, { type: 'task', label: t.title, notes: t.notes || '', id: t.id, done: t.status === 'done' });
    });

    var cells = [];
    for (var pad = 0; pad < startDay; pad++) cells.push('<div class="pw-cell is-empty"></div>');
    for (var day = 1; day <= daysInMonth; day++) {
      var date = ymd(new Date(month.getFullYear(), month.getMonth(), day));
      var chips = (byDate[date] || []).map(chipHtml).join('');
      cells.push(
        '<div class="pw-cell' + (date === today ? ' is-today' : '') + '" data-date="' + date + '">'
        + '<button type="button" class="pw-cell-add" data-add="' + date + '" '
        + 'aria-label="Add an entry on ' + date + '">+</button>'
        + '<div class="pw-cell-n">' + day + '</div>'
        + '<div class="pw-cell-chips">' + chips + '</div>'
        + '</div>');
    }

    var dows = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    host.innerHTML =
      '<div class="pw-cal-head">'
      + '<button type="button" class="pw-cal-nav" data-month="-1" aria-label="Previous month">'
      + '<i class="bi bi-chevron-left"></i></button>'
      + '<h2 class="pw-cal-title">' + esc(monthLabel(month)) + '</h2>'
      + '<button type="button" class="pw-cal-nav" data-month="1" aria-label="Next month">'
      + '<i class="bi bi-chevron-right"></i></button>'
      + '<button type="button" class="pw-cal-today" data-month="0">Today</button>'
      + '</div>'
      + monthSummary(month)
      + '<div class="pw-cal-dows">' + dows.map(function (d) {
        return '<div>' + d + '</div>';
      }).join('') + '</div>'
      + '<div class="pw-cal">' + cells.join('') + '</div>';
  }

  /**
   * What this month actually holds, in a line: weddings, deadlines, and the
   * planner's own diary. It replaced a legend, which explained the colours of
   * three chip types a planner can already tell apart, and said nothing about
   * the month they were looking at.
   */
  function monthSummary(month) {
    var y = month.getFullYear(), m = month.getMonth();
    // A bare 'YYYY-MM-DD' (every day the server sends) by its parts: read by
    // new Date() it is UTC midnight, and a Dec 1 wedding counted in
    // November's line in the Americas while the grid showed it on Dec 1.
    function inMonth(iso) {
      if (!iso) return false;
      var p = /^(\d{4})-(\d{2})-\d{2}$/.exec(String(iso).trim());
      if (p) return Number(p[1]) === y && Number(p[2]) - 1 === m;
      var d = new Date(iso);
      return d.getFullYear() === y && d.getMonth() === m;
    }
    var days = state.events.filter(function (e) { return inMonth(e.eventDate); }).length;
    var deadlines = state.events.filter(function (e) { return inMonth(e.rsvpDeadline); }).length;
    var diary = state.notes.filter(function (n) { return inMonth(n.date); }).length;
    var tasks = (state.tasks || []).filter(function (t) { return t.status === 'open' && inMonth(t.due); }).length;

    function part(n, one, many, cls) {
      return '<span class="pw-sum' + (n ? '' : ' is-zero') + '">'
        + '<i class="pw-dot ' + cls + '"></i><b>' + n + '</b> ' + (n === 1 ? one : many) + '</span>';
    }
    return '<div class="pw-summary">'
      + part(days, 'wedding day', 'wedding days', 'is-event')
      + part(deadlines, 'RSVP deadline', 'RSVP deadlines', 'is-deadline')
      + part(diary, 'diary entry', 'diary entries', 'is-note')
      + part(tasks, 'task due', 'tasks due', 'is-task')
      + '<span class="pw-summary-hint">Click any day to add to your diary</span>'
      + '</div>';
  }

  function chipHtml(c) {
    if (c.type === 'task') {
      // Click to tick it off (or back on); the tick is the chip's whole job here.
      return '<button type="button" class="pw-chip is-task' + (c.done ? ' is-done' : '') + '" '
        + 'data-task-toggle="' + esc(c.id) + '" title="' + esc(c.label + (c.notes ? '\n' + c.notes : ''))
        + (c.done ? ' (done - click to reopen)' : ' (click to mark done)') + '">'
        + '<i class="bi ' + (c.done ? 'bi-check2-square' : 'bi-square') + '" aria-hidden="true"></i>'
        + '<span>' + esc(c.label) + '</span></button>';
    }
    if (c.type === 'note') {
      return '<span class="pw-chip is-note is-' + esc(c.kind || 'note') + '" '
        + 'title="' + esc(c.label) + '" data-note="' + esc(c.id) + '">'
        + '<span>' + esc(c.label) + '</span>'
        + '<button type="button" class="pw-chip-x" data-del="' + esc(c.id) + '" '
        + 'aria-label="Remove">&times;</button></span>';
    }
    var solid = clientColor(c.client, 'ink');
    var soft = clientColor(c.client, 'soft');
    var style = c.type === 'event'
      ? '--c:' + solid + ';--bg:' + solid + ';--fg:#fff'
      : '--c:' + solid + ';--bg:' + soft + ';--fg:' + solid;
    return '<a class="pw-chip is-' + c.type + '" style="' + style + '" '
      + 'href="/dashboard.html?formId=' + encodeURIComponent(c.id) + '" '
      + 'title="' + esc(c.label) + '"><span>' + esc(c.label) + '</span></a>';
  }

  /* =======================================================================
     Needs you
     ======================================================================= */

  /**
   * Events inside their alert window (task #696) with replies still missing.
   * The window is the planner's own - a default, or the event's own timing -
   * instead of the fixed 45 days this used to be. Sorted by the attention
   * score, which still weighs deadline pressure against the replies missing.
   */
  function eventsNeedingYou() {
    return state.events
      .filter(function (e) {
        var inWindow = e.alert ? e.alert.inAlert : e.attention > 0;
        var missing = e.responseRate == null || e.responseRate < 1;
        return inWindow && missing && !(e.alert && e.alert.dismissed);
      })
      .sort(function (a, b) { return b.attention - a.attention; });
  }

  // Task #826: the ones the planner dismissed at their current alert point.
  function eventsDismissed() {
    return state.events.filter(function (e) {
      return e.alert && e.alert.inAlert && e.alert.dismissed && (e.responseRate == null || e.responseRate < 1);
    });
  }

  function setChaseDismissed(id, dismiss) {
    var ev = state.events.filter(function (e) { return e.id === id; })[0];
    if (!ev || !ev.alert) return;
    var before = ev.alert.dismissed;
    ev.alert.dismissed = dismiss;   // optimistic, put back if the save fails
    renderAttention();
    if (dismiss && ev.alert.window != null) toast('Hidden until its next alert', 'success');
    api('/api/planner/alerts/event/' + encodeURIComponent(id) + '/dismiss', {
      method: 'PUT', body: JSON.stringify({ dismiss: !!dismiss })
    }).catch(function (e) { ev.alert.dismissed = before; renderAttention(); toast(e.message, 'error'); });
  }

  function renderAttention() {
    var host = $('pwAttention');
    var list = eventsNeedingYou();
    var open = (state.tasks || []).filter(function (t) { return t.status === 'open'; });

    var badge = $('pwAttentionCount');
    badge.textContent = list.length + open.length;
    badge.classList.toggle('d-none', list.length + open.length === 0);

    var w = (state.alerts && state.alerts.windows) || [30, 14, 3];
    // Task #806: one header bar (title, one line, Alert timing on the right,
    // its settings opening as a floating card over the page instead of
    // pushing it down), then two cards - the events to chase, and the tasks.
    var head = '<div class="pw-ny">'
      + '<div class="pw-ny-bar">'
      + '<div class="pw-ny-bar-text"><h2 class="pw-ny-title">Needs you</h2>'
      + '<p class="pw-ny-lead">Events inside their alert window (' + esc(w.join(', ')) + ' days before the RSVP '
      + 'deadline) with replies still missing, then your own tasks.</p></div>'
      + '<div class="pw-ny-bar-actions">'
      + '<button type="button" class="btn-org-outline btn-sm" data-alerts-toggle aria-expanded="false" aria-controls="pwAlertSettings">'
      + '<i class="bi bi-bell"></i>Alert timing <i class="bi bi-chevron-down pw-ny-caret"></i></button>'
      + '<div class="pw-alerts d-none" id="pwAlertSettings" role="dialog" aria-label="Alert timing">' + alertSettingsHtml() + '</div>'
      + '</div></div>';

    var events = nyCardOpen('events', 'pwEventsTitle', 'bi-send', 'Events to chase', list.length, '')
      + (list.length
        ? '<div class="pw-todo">' + list.map(todoRow).join('') + '</div>'
        : '<div class="pw-ny-empty"><span class="pw-ny-empty-icon"><i class="bi bi-check2-circle"></i></span>'
          + '<div><div class="pw-ny-empty-title">No event needs chasing</div>'
          + '<div class="pw-ny-empty-text">No event is inside its alert window with replies still missing.</div></div></div>')
      + dismissedHtml()
      + '</div></section>';

    host.innerHTML = head + events + tasksHtml() + '</div>';
  }

  /*
   * Task #826: both Needs-you cards fold away from their header and stay the
   * way the planner left them (this browser), open until they are folded. The
   * count stays in the header, so a folded card still says how much is in it.
   * The body is closed by nyCardOpen and by the caller ('</div></section>').
   */
  var NY_FOLD_KEY = 'pwNeedsYouFolded';
  function nyFolded() {
    try { return JSON.parse(localStorage.getItem(NY_FOLD_KEY) || '{}') || {}; } catch (e) { return {}; }
  }
  function nyCardOpen(key, titleId, icon, title, count, extraClass) {
    var folded = nyFolded()[key] === true;
    var bodyId = titleId + 'Body';
    return '<section class="pw-ny-card' + (extraClass ? ' ' + extraClass : '') + (folded ? ' is-folded' : '') + '" aria-labelledby="' + titleId + '">'
      + '<header class="pw-ny-card-head"><h3 class="pw-ny-card-title">'
      + '<button type="button" class="pw-ny-fold" id="' + titleId + '" data-ny-fold="' + key + '" '
      + 'aria-expanded="' + (folded ? 'false' : 'true') + '" aria-controls="' + bodyId + '">'
      + '<i class="bi ' + icon + '"></i>' + esc(title)
      + (count ? '<span class="pw-ny-count">' + count + '</span>' : '')
      + '<i class="bi bi-chevron-down pw-ny-fold-caret" aria-hidden="true"></i></button></h3></header>'
      + '<div class="pw-ny-card-body" id="' + bodyId + '"' + (folded ? ' hidden' : '') + '>';
  }
  function toggleNyFold(btn) {
    var key = btn.getAttribute('data-ny-fold');
    var card = btn.closest('.pw-ny-card');
    var body = document.getElementById(btn.getAttribute('aria-controls'));
    var fold = btn.getAttribute('aria-expanded') === 'true';
    btn.setAttribute('aria-expanded', fold ? 'false' : 'true');
    if (card) card.classList.toggle('is-folded', fold);
    if (body) body.hidden = fold;
    var all = nyFolded();
    all[key] = fold;
    try { localStorage.setItem(NY_FOLD_KEY, JSON.stringify(all)); } catch (e) { /* private mode */ }
  }

  function dismissedHtml() {
    var gone = eventsDismissed();
    if (!gone.length) return '';
    return '<details class="pw-task-done pw-chase-dismissed"><summary>Dismissed until their next alert (' + gone.length + ')</summary>'
      + '<div class="pw-task-list">' + gone.map(function (e) {
        return '<div class="pw-task is-done"><div class="pw-task-main"><span class="pw-task-title">' + esc(e.title) + '</span>'
          + '<span class="pw-task-meta">' + esc(e.alert.window + '-day alert') + '</span></div>'
          + '<button type="button" class="btn btn-link btn-sm" data-chase-restore="' + esc(e.id) + '">Put back</button></div>';
      }).join('') + '</div></details>';
  }

  /* ---- Alert timing (task #696) ---------------------------------------- */

  function alertSettingsHtml() {
    var a = state.alerts || { windows: [30, 14, 3], emails: false };
    var upcoming = state.events.filter(function (e) {
      var h = e.deadlineInDays == null ? e.eventInDays : e.deadlineInDays;
      return h != null && h >= 0;
    });
    return '<div class="pw-alerts-row">'
      + '<label class="pw-alerts-label" for="pwAlertDays">Put an event here this many days before its RSVP deadline</label>'
      + '<div class="pw-alerts-inline">'
      + '<input type="text" class="form-control form-control-sm" id="pwAlertDays" inputmode="numeric" '
      + 'value="' + esc(a.windows.join(', ')) + '" placeholder="30, 14, 3" aria-describedby="pwAlertDaysHint">'
      + '<button type="button" class="btn-org-primary btn-sm" data-alerts-save>Save</button>'
      + '</div>'
      + '<div class="pw-alerts-hint" id="pwAlertDaysHint">Up to five numbers, e.g. 30, 14, 3. An event with no deadline counts from its date. '
      + 'Events with their own timing below keep it.</div>'
      + '</div>'
      + '<label class="pw-alerts-switch">'
      + '<input type="checkbox" class="form-check-input" id="pwAlertEmails"' + (a.emails ? ' checked' : '') + '>'
      + '<span><b>Email me</b> on the mornings an event reaches one of these points, and when a task falls due. '
      + 'Off unless you turn it on.</span></label>'
      + (upcoming.length
        ? '<details class="pw-alerts-events"><summary>Timing for one event</summary>'
          + upcoming.map(function (e) {
            var own = e.alertWindows && e.alertWindows.length;
            return '<div class="pw-alerts-ev" data-alert-ev="' + esc(e.id) + '">'
              + '<span class="pw-alerts-ev-title">' + esc(e.title) + '</span>'
              + '<input type="text" class="form-control form-control-sm" inputmode="numeric" '
              + 'value="' + esc(own ? e.alertWindows.join(', ') : '') + '" '
              + 'placeholder="Default (' + esc(a.windows.join(', ')) + ')" aria-label="Alert days for ' + esc(e.title) + '">'
              + '<button type="button" class="btn-org-outline btn-sm" data-alert-ev-save>Save</button>'
              + (own ? '<button type="button" class="btn-org-outline btn-sm" data-alert-ev-clear>Use default</button>' : '')
              + '</div>';
          }).join('')
          + '</details>'
        : '');
  }

  function saveAlertDays() {
    var input = $('pwAlertDays');
    api('/api/planner/alerts', { method: 'PUT', body: JSON.stringify({ windows: input.value }) })
      .then(function (res) { state.alerts = res.alerts; return reloadKeepingSettingsOpen('Alert timing saved'); })
      .catch(function (e) { toast(e.message, 'error'); });
  }

  function saveAlertEmails(on) {
    api('/api/planner/alerts', { method: 'PUT', body: JSON.stringify({ emails: !!on }) })
      .then(function (res) {
        state.alerts = res.alerts;
        toast(on ? 'Alert emails are on' : 'Alert emails are off', 'success');
      })
      .catch(function (e) { toast(e.message, 'error'); $('pwAlertEmails').checked = !on; });
  }

  function saveEventAlert(formId, value) {
    api('/api/planner/alerts/event/' + encodeURIComponent(formId), {
      method: 'PUT', body: JSON.stringify({ windows: value })
    }).then(function () { return reloadKeepingSettingsOpen(value === null ? 'Back to your default timing' : 'Timing saved for this event'); })
      .catch(function (e) { toast(e.message, 'error'); });
  }

  // The windows decide which events are in the list, so a change re-reads the
  // workspace rather than guessing; the settings stay open where they were.
  function reloadKeepingSettingsOpen(message) {
    return load().then(function () {
      renderAll();
      var s = $('pwAlertSettings');
      if (s) s.classList.remove('d-none');
      var t = document.querySelector('[data-alerts-toggle]');
      if (t) t.setAttribute('aria-expanded', 'true');
      if (message) toast(message, 'success');
    });
  }

  /* ---- Tasks (task #696) ----------------------------------------------- */

  function tasksHtml() {
    var tasks = state.tasks || [];
    var open = tasks.filter(function (t) { return t.status === 'open'; });
    var done = tasks.filter(function (t) { return t.status === 'done'; });
    var eventOpts = state.events.filter(function (e) { return !e.isTemplate; }).map(function (e) {
      return '<option value="' + esc(e.id) + '">' + esc(e.title) + '</option>';
    }).join('');
    return nyCardOpen('tasks', 'pwTasksTitle', 'bi-check2-square', 'Your tasks', open.length, 'pw-tasks')
      // Task #818: a button above the list opens the add form in a window,
      // instead of the form (and its notes box) sitting open over the list.
      + '<div class="pw-task-add-row"><button type="button" class="btn-org-primary btn-sm" data-task-new>'
      + '<i class="bi bi-plus-lg"></i>Add task</button></div>'
      + (open.length
        ? '<div class="pw-task-list">' + open.map(taskRow).join('') + '</div>'
        : '<p class="pw-tasks-empty">No open tasks. Add one with the button above; with a date it also shows on your Season calendar.</p>')
      + (done.length
        ? '<details class="pw-task-done"><summary>Done (' + done.length + ')</summary>'
          + '<div class="pw-task-list">' + done.map(taskRow).join('') + '</div></details>'
        : '')
      + '</div></section>';
  }

  function taskRow(t) {
    var ev = t.formId ? state.events.filter(function (e) { return e.id === t.formId; })[0] : null;
    var today = ymd(new Date());
    var overdue = t.status === 'open' && t.due && t.due < today;
    var dueTxt = t.due
      ? '<span class="pw-task-due' + (overdue ? ' is-past' : '') + (t.due === today ? ' is-today' : '') + '">'
        + (overdue ? 'Overdue - ' : t.due === today ? 'Today - ' : 'Due ') + esc(t.due) + '</span>'
      : '';
    var isDone = t.status === 'done';
    return '<div class="pw-task' + (isDone ? ' is-done' : '') + '" data-task-id="' + esc(t.id) + '"'
      + (ev ? ' style="--c:' + clientColor(ev.client, 'ink') + '"' : '') + '>'
      + '<button type="button" class="pw-task-check" data-task-toggle="' + esc(t.id) + '" '
      + 'aria-label="' + (isDone ? 'Mark as not done' : 'Mark done') + '" aria-pressed="' + (isDone ? 'true' : 'false') + '">'
      + '<i class="bi ' + (isDone ? 'bi-check-circle-fill' : 'bi-circle') + '"></i></button>'
      + '<div class="pw-task-main"><span class="pw-task-title">' + esc(t.title) + '</span>'
      + (t.notes ? '<span class="pw-task-notes">' + esc(t.notes) + '</span>' : '')
      + '<span class="pw-task-meta">' + dueTxt
      + (ev ? '<a href="/dashboard.html?formId=' + encodeURIComponent(ev.id) + '">' + esc(ev.title) + '</a>' : '')
      + '</span>'
      + '</div>'
      // Task #826 round 3 (Esther: the notes button "should just be an edit
      // button to edit all features of the task", and the x read as a
      // dismiss but deleted the task): one pencil opens the task's window,
      // every field editable, and Delete lives in there behind a confirm.
      + '<button type="button" class="pw-task-x pw-task-edit-btn" data-task-edit="' + esc(t.id) + '" aria-label="Edit task" title="Edit task">'
      + '<i class="bi bi-pencil"></i></button>'
      + '</div>';
  }

  function taskById(id) { return (state.tasks || []).filter(function (t) { return t.id === id; })[0]; }

  function setTaskStatus(id, status) {
    var t = taskById(id);
    if (!t) return;
    var before = t.status;
    // Optimistic: the tick is instant, and put back if the save fails.
    if (status === 'dismissed') state.tasks = state.tasks.filter(function (x) { return x.id !== id; });
    else t.status = status;
    renderSeason(); renderAttention();
    api('/api/planner/tasks/' + encodeURIComponent(id), { method: 'PATCH', body: JSON.stringify({ status: status }) })
      .catch(function (e) {
        if (status === 'dismissed') state.tasks.push(t); else t.status = before;
        renderSeason(); renderAttention();
        toast(e.message, 'error');
      });
  }

  // Task #818: the add form, in a window. Same fields and the same submit
  // handler (#pwTaskAdd) as the inline form it replaced; the event list is
  // read when the window opens, so a newly made event is offered. With a task
  // (task #826 round 3) it is that task's Edit window: every field filled in,
  // Save instead of Add, and Delete on the left.
  function openTaskModal(task) {
    var old = $('pwTaskModal');
    if (old) old.remove();
    var editing = !!task;
    var eventOpts = state.events.filter(function (e) { return !e.isTemplate; }).map(function (e) {
      return '<option value="' + esc(e.id) + '"' + (editing && task.formId === e.id ? ' selected' : '') + '>' + esc(e.title) + '</option>';
    }).join('');
    var wrap = document.createElement('div');
    wrap.innerHTML = '<div class="modal fade" id="pwTaskModal" tabindex="-1" aria-labelledby="pwTaskModalTitle" aria-hidden="true">'
      + '<div class="modal-dialog modal-dialog-centered"><div class="modal-content">'
      + '<form id="pwTaskAdd" autocomplete="off"' + (editing ? ' data-task-id="' + esc(task.id) + '"' : '') + '>'
      + '<div class="modal-header"><h5 class="modal-title" id="pwTaskModalTitle"><i class="bi bi-check2-square me-2"></i>' + (editing ? 'Edit task' : 'New task') + '</h5>'
      + '<button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Cancel"></button></div>'
      + '<div class="modal-body">'
      + '<label class="form-label small fw-semibold" for="pwTaskTitle">Task</label>'
      + '<input type="text" class="form-control mb-3" id="pwTaskTitle" name="title" maxlength="140" '
      + 'placeholder="Final headcount to the caterer, chase dietary answers…" required' + (editing ? ' value="' + esc(task.title) + '"' : '') + '>'
      + '<div class="row g-2 mb-3"><div class="col-sm-5"><label class="form-label small fw-semibold" for="pwTaskDue">Due date <span class="text-muted fw-normal">(optional)</span></label>'
      + '<input type="date" class="form-control" id="pwTaskDue" name="due"' + (editing && task.due ? ' value="' + esc(task.due) + '"' : '') + '></div>'
      + '<div class="col-sm-7"><label class="form-label small fw-semibold" for="pwTaskEvent">Event <span class="text-muted fw-normal">(optional)</span></label>'
      + '<select class="form-select" id="pwTaskEvent" name="formId"><option value="">No event</option>' + eventOpts + '</select></div></div>'
      + '<label class="form-label small fw-semibold" for="pwTaskNotes">Notes <span class="text-muted fw-normal">(optional)</span></label>'
      + '<textarea class="form-control" id="pwTaskNotes" name="notes" rows="3" maxlength="2000" placeholder="What was agreed, who to call…">' + (editing ? esc(task.notes || '') : '') + '</textarea>'
      + '</div>'
      + '<div class="modal-footer">'
      + (editing ? '<button type="button" class="btn btn-link btn-sm text-danger me-auto px-0" data-task-delete="' + esc(task.id) + '"><i class="bi bi-trash me-1"></i>Delete task</button>' : '')
      + '<button type="button" class="btn btn-outline-secondary btn-sm" data-bs-dismiss="modal">Cancel</button>'
      + '<button type="submit" class="btn-org-primary btn-sm">' + (editing ? '<i class="bi bi-check-lg"></i>Save' : '<i class="bi bi-plus-lg"></i>Add') + '</button></div>'
      + '</form></div></div></div>';
    var modal = wrap.firstChild;
    document.body.appendChild(modal);
    modal.addEventListener('shown.bs.modal', function () { var t = $('pwTaskTitle'); if (t) t.focus(); });
    modal.addEventListener('hidden.bs.modal', function () { modal.remove(); });
    window.bootstrap.Modal.getOrCreateInstance(modal).show();
  }

  function addTaskFromForm(form) {
    var fd = new FormData(form);
    var body = { title: fd.get('title'), notes: fd.get('notes') || '', due: fd.get('due') || null, formId: fd.get('formId') || null };
    if (!String(body.title || '').trim()) return;
    var btn = form.querySelector('button[type="submit"]');
    btn.disabled = true;
    var editId = form.getAttribute('data-task-id');
    if (editId) {
      // The Edit window (task #826): every field in one PATCH.
      return api('/api/planner/tasks/' + encodeURIComponent(editId), { method: 'PATCH', body: JSON.stringify(body) })
        .then(function (res) {
          var t = taskById(editId);
          if (t && res && res.task) { t.title = res.task.title; t.notes = res.task.notes; t.due = res.task.due; t.formId = res.task.formId; }
          renderSeason(); renderAttention();
          var modal = $('pwTaskModal');
          if (modal && window.bootstrap) window.bootstrap.Modal.getOrCreateInstance(modal).hide();
        })
        .catch(function (e) { toast(e.message, 'error'); btn.disabled = false; });
    }
    api('/api/planner/tasks', { method: 'POST', body: JSON.stringify(body) })
      .then(function (res) {
        state.tasks.push(res.task);
        renderSeason(); renderAttention();
        var modal = $('pwTaskModal');
        if (modal && window.bootstrap) window.bootstrap.Modal.getOrCreateInstance(modal).hide();
      })
      .catch(function (e) { toast(e.message, 'error'); btn.disabled = false; });
  }

  /**
   * One thing that needs doing.
   *
   * The list this replaced said which events were urgent and then left the
   * planner to go and find them. Every row here ends in the two things you do
   * about it — chase the guests who have not replied, or open the event — so
   * the view that tells you what is wrong is also the one you fix it from.
   */
  function todoRow(e) {
    var pct = e.responseRate != null ? Math.round(e.responseRate * 100) : null;
    var missing = (e.invited != null && e.responses != null) ? Math.max(0, e.invited - e.responses) : null;
    var soon = e.deadlineInDays != null && e.deadlineInDays >= 0 && e.deadlineInDays <= 7;
    var overdue = e.deadlineInDays != null && e.deadlineInDays < 0;

    var when = e.deadlineInDays != null
      ? '<span class="pw-todo-due' + (soon ? ' is-soon' : '') + (overdue ? ' is-past' : '') + '">'
        + esc(overdue ? 'RSVP closed ' + relDays(e.deadlineInDays) : 'RSVPs close ' + relDays(e.deadlineInDays))
        + '</span>'
      : '<span class="pw-todo-due is-none" title="No RSVP deadline set on this event">No deadline set</span>';
    // Task #696: which of its alert points it is inside, and whose timing.
    if (e.alert && e.alert.window != null) {
      when += '<span class="pw-todo-alert" title="' + esc(e.alertWindows ? 'This event’s own alert timing' : 'Your default alert timing') + '">'
        + '<i class="bi bi-bell"></i>' + esc(e.alert.window + '-day alert') + '</span>';
    }

    return '<article class="pw-todo-row" style="--c:' + clientColor(e.client, 'ink') + '">'
      + '<div class="pw-todo-main">'
      + '<div class="pw-todo-top">'
      + '<a class="pw-todo-title" href="/dashboard.html?formId=' + encodeURIComponent(e.id) + '">'
      + esc(e.title) + '</a>'
      + when
      + '</div>'
      + '<div class="pw-todo-sub">'
      + (e.eventDate ? esc(e.eventDate) + ' &middot; ' + esc(relDays(e.eventInDays)) : 'No date set')
      + (e.location ? ' &middot; ' + esc(e.location) : '')
      + '</div>'
      + '<div class="pw-todo-progress">'
      // No bar when there is no invited list to measure against: a track drawn
      // at 0% reads as "nobody has replied", which is the opposite of true.
      + (pct == null ? '' : '<span class="pw-bar"><i style="width:' + pct + '%"></i></span>')
      + '<span class="pw-todo-count">'
      + (missing != null
          ? '<b>' + missing + '</b> of ' + e.invited + ' still to reply'
          : '<b>' + (e.responses || 0) + '</b> replies so far')
      + '</span>'
      + '</div>'
      + '</div>'
      + '<div class="pw-todo-actions">'
      + '<a class="btn-org-primary btn-sm" href="/dashboard.html?formId=' + encodeURIComponent(e.id) + '&tab=campaigns">'
      + '<i class="bi bi-send"></i>Chase replies</a>'
      + '<a class="btn-org-outline btn-sm" href="/dashboard.html?formId=' + encodeURIComponent(e.id) + '">Open event</a>'
      // Task #826: off the list until the next alert point.
      + '<button type="button" class="pw-task-x pw-chase-x" data-chase-dismiss="' + esc(e.id) + '" '
      + 'aria-label="Dismiss until the next alert" title="Dismiss until the next alert"><i class="bi bi-x-lg"></i></button>'
      + '</div>'
      + '</article>';
  }

  function empty(title, body) {
    return '<div class="pw-empty"><h3>' + esc(title) + '</h3><p>' + esc(body) + '</p></div>';
  }

  /* =======================================================================
     Interaction
     ======================================================================= */

  // What is happening, then what you own, then how the studio is set up.
  //
  // Websites and registries used to be two more tabs. A client's website is a
  // fact about that client's wedding, not a separate list to cross-reference,
  // so both now appear on the event's own row under the couple's name.
  // 'account' is in this list but NOT in the rail (task #745): it is reached
  // from the pill at the foot of the sidebar, which is where an account
  // belongs, and switchView tolerates a view with no nav button - the loop
  // below simply matches none.
  var VIEWS = ['season', 'events', 'attention',
               'branding', 'templates', 'billing', 'account'];

  function switchView(view) {
    if (VIEWS.indexOf(view) === -1) return;
    state.view = view;
    VIEWS.forEach(function (v) {
      var panel = $('pw' + v.charAt(0).toUpperCase() + v.slice(1));
      if (panel) panel.classList.toggle('d-none', v !== view);
    });
    document.querySelectorAll('.org-nav-item[data-view]').forEach(function (b) {
      var on = b.getAttribute('data-view') === view;
      b.classList.toggle('active', on);
      b.setAttribute('aria-current', on ? 'true' : 'false');
    });
    loadStudioTool(view);
  }

  /* -----------------------------------------------------------------------
     Branding / Templates / Billing.

     These three are my-projects.js's dialogs. Rather than owning a second,
     English-only copy of three translated forms, the studio ADOPTS them: the
     body and footer of each injected modal is moved into the matching panel
     once, ids and all, so every handler my-projects.js already wires keeps
     working and every string stays translated.

     Two consequences are handled here and nowhere else:
       - its handlers still call Modal.show()/hide() on the now-empty shells,
         so those calls are made no-ops for these three ids only;
       - a "Close" button means nothing in a panel, so it is dropped.
     ----------------------------------------------------------------------- */

  var TOOLS = {
    branding: function () { return mp() && mp().openPlannerBranding(); },
    templates: function () { return mp() && mp().openTemplateLibrary(); },
    billing: function () { return mp() && mp().openPlannerBilling(); },
    // Events FETCH through my-projects.js and are DRAWN here. See the comment
    // on #pwEvents in planner.html. loadForms() pulls the websites and the
    // registries too, because the row needs all three to pair them.
    events: function () { return mp() && mp().loadForms(); },
    // Task #745. Filled EVERY time rather than once: a subscription cancelled
    // in the Stripe portal has to read as cancelled when the planner comes
    // back to this panel, and the credit balance moves on its own.
    account: function () { return window.mountAccountPanel && window.mountAccountPanel(); }
  };
  function mp() { return window.myProjects || null; }

  function adoptTools() {
    // Deliberately NOT latched (task #745): the account dialog is injected on
    // first use, so the first run of this has nothing to adopt for it. The
    // per-panel guard below — a panel that already holds a .pw-pane-body is
    // skipped — is the one that matters, and the bootstrap patch has its own.
    var host = document.getElementById('plannerModalsHost');
    if (!host) return;                       // planner-modals.js has not injected yet
    var panels = document.querySelectorAll('[data-adopt]');
    if (!panels.length) return;

    panels.forEach(function (panel) {
      var modal = document.getElementById(panel.getAttribute('data-adopt'));
      // A panel may already hold its own lead paragraph; what must not happen
      // twice is the adoption itself.
      if (!modal || panel.querySelector('.pw-pane-body')) return;

      // The dialog TITLE is deliberately left behind. In a panel the tab above
      // is already the heading, and adopting it gave every pane a second copy
      // of its own tab label. It stays in the shell so openPlannerBranding(),
      // which rewrites it on every open, still has an element to write to.
      var body = modal.querySelector('.modal-body');
      if (body) {
        var wrap = document.createElement('div');
        wrap.className = 'pw-pane-body';
        while (body.firstChild) wrap.appendChild(body.firstChild);
        panel.appendChild(wrap);
      }

      var footer = modal.querySelector('.modal-footer');
      if (footer) {
        var bar = document.createElement('div');
        bar.className = 'pw-pane-foot';
        Array.prototype.slice.call(footer.children).forEach(function (child) {
          if (child.getAttribute('data-bs-dismiss') === 'modal') return;
          bar.appendChild(child);
        });
        if (bar.childElementCount) panel.appendChild(bar);
      }
    });

    // Task #745: the row the account MENU carried that the rail does not.
    // Added after adoption so it sits under the adopted body, and only once.
    // No "My Events" beside it (round 2): a planner's events are the studio.
    var acct = document.getElementById('pwAccount');
    if (acct && acct.querySelector('.pw-pane-body') && !acct.querySelector('.pw-account-foot')) {
      var foot = document.createElement('div');
      foot.className = 'pw-pane-foot pw-account-foot';
      foot.innerHTML =
        '<button type="button" class="btn-org-outline" id="pwSignOut">'
        + '<i class="bi bi-box-arrow-right"></i> Sign Out</button>';
      acct.appendChild(foot);
      var out = foot.querySelector('#pwSignOut');
      if (out) {
        out.addEventListener('click', function () {
          if (mp() && typeof mp().signOut === 'function') mp().signOut();
        });
      }
    }

    // Same suppression for both show() and hide(): every call site in
    // my-projects.js goes through getOrCreateInstance, including the two that
    // hide a dialog after a successful save.
    if (window.bootstrap && bootstrap.Modal && !bootstrap.Modal.__pwPatched) {
      var ADOPTED = ['plannerBrandingModal', 'plannerTemplateLibraryModal', 'plannerBillingModal',
                     'accountModal'];
      var noop = function () { return this; };
      var inert = { show: noop, hide: noop, toggle: noop, handleUpdate: noop, dispose: noop };
      var real = bootstrap.Modal.getOrCreateInstance.bind(bootstrap.Modal);
      bootstrap.Modal.getOrCreateInstance = function (el, cfg) {
        if (el && ADOPTED.indexOf(el.id) !== -1) return inert;
        return real(el, cfg);
      };
      bootstrap.Modal.__pwPatched = true;
    }
  }

  // Each tool loads the first time its tab is opened, and Billing reloads
  // every time — it is the one whose numbers move while you are on the page.
  var loadedTools = {};
  function loadStudioTool(view) {
    var run = TOOLS[view];
    if (!run) return;
    // The account dialog builds itself on first use, so it has to exist BEFORE
    // adoption or there is no body to move into the panel (task #745).
    if (view === 'account' && window.mountAccountPanel) {
      try { window.mountAccountPanel(); } catch (e) { /* the panel shows its own error */ }
    }
    adoptTools();

    // Fetch once; DRAW every time. Coming back to a tab has to repaint from
    // whatever the cached data says now — a client filed under a couple on one
    // tab has to be grouped under them on the next — but it must not pay for
    // the query again. Billing is the exception: its numbers move while you
    // are on the page, so it refetches.
    var first = !loadedTools[view];
    loadedTools[view] = true;
    // Task #819: something on screen at once - skeleton rows until the first
    // list arrives (the remembered one, usually, within a moment).
    if (view === 'events' && first && !mpForms().length) {
      var host = $('pwEventList');
      if (host) host.innerHTML = eventSkeleton();
    }
    try {
      var done = (first || view === 'billing' || view === 'account')
        ? Promise.resolve(run()) : Promise.resolve();
      if (view === 'billing') done = done.then(mountBillingBar).then(renderBilling);
      if (view === 'events') done = done.then(mountFilters).then(renderEvents);
      done.catch(function () {});
    } catch (e) { /* the panel shows its own error */ }
  }

  /* =======================================================================
     Events, websites and registries — the studio's own rows.

     The DATA is my-projects.js's: loadForms(), loadSites() and
     loadRegistries() fetch it and leave it on myProjects.forms/.sites/
     .registries. What is drawn here is the org dashboard's row, because a
     planner reads a list of twenty client events, and twenty cards three
     across is a page you scroll rather than a page you scan.

     Every control still calls the same my-projects.js handler the card called,
     so nothing a host can do to an event is missing from a planner's row.
     ======================================================================= */

  var filters = { q: '', client: '', when: '', tier: '', group: 'client' };

  function mpForms() { return (mp() && mp().forms) || []; }

  // Task #819: the Events tab used to wait for my-projects.js to finish
  // everything - the events, then the co-owned ones, then response counts,
  // then portfolio stats, then websites and registries - before drawing a
  // row. It now draws from the remembered list at once, again when the events
  // themselves are in, and a last time when the counts and pairings land.
  document.addEventListener('mp:forms-loaded', function () {
    if (!loadedTools.events) return;
    mountFilters();
    renderEvents();
  });

  function eventSkeleton() {
    var row = '<div class="pw-skel-row" aria-hidden="true"><span class="pw-skel pw-skel-title"></span>'
      + '<span class="pw-skel pw-skel-meta"></span><span class="pw-skel pw-skel-chip"></span></div>';
    return '<div class="pw-skel-list" role="status" aria-label="Loading events">' + row + row + row + row + '</div>';
  }

  function ico(title, inner, cls) {
    return '<button type="button" class="btn-sm-icon' + (cls ? ' ' + cls : '') + '" title="' + esc(title)
      + '" aria-label="' + esc(title) + '">' + inner + '</button>';
  }

  // ---------------------------------------------------------------- events

  function eventOf(id) {
    for (var i = 0; i < state.events.length; i++) {
      if (state.events[i].id === id) return state.events[i];
    }
    return null;
  }

  function formMeta(f) {
    var m = mp();
    var ev = eventOf(f.id) || {};
    var paid = !!(f.paymentStatus && ((f.paymentStatus.basic && f.paymentStatus.basic.paid)
      || (f.paymentStatus.premium && f.paymentStatus.premium.paid)));
    var signature = !!(f.paymentStatus && f.paymentStatus.basic_signature
      && f.paymentStatus.basic_signature.paid) || f.planType === 'basic_signature';
    return {
      id: f.id,
      title: f.title || 'Untitled event',
      client: ev.client || '',
      ev: ev,
      paid: paid || signature,
      signature: signature,
      coOwned: !!f._coOwned,
      notif: !!(f.emailNotifications && f.emailNotifications.enabled),
      isTemplate: !!f.isTemplate,
      // Replies, not rows: responseCount also counts an uploaded guest list.
      responses: (typeof f._liveResponseCount === 'number' ? f._liveResponseCount
        : window.FeatureGates?.getRepliesReceived ? window.FeatureGates.getRepliesReceived(f) : (f.responseCount || 0)),
      stats: f._stats || null,
      publicUrl: (m && m._publicFormUrl) ? m._publicFormUrl(f.id, f) : '',
      editLink: (m && m._getEditLinkForForm) ? m._getEditLinkForForm(f) : ('/builder.html?formId=' + f.id),
      dashLink: (m && m._getDashboardLink) ? m._getDashboardLink(f.id) : ('/dashboard.html?formId=' + f.id)
    };
  }

  /* A client's website and gift registry, on the client's own row.

     Pairing is by the SAME join My Events uses — the RSVP form id the wedding
     builder stores on the site, and the registry's own formId — never by
     matching names, because "Sarah & David" and "Sarah and David" are one
     couple to a planner and two strings to a computer. */
  function pairAssets(rows) {
    var m = mp(), byId = {}, loose = [];
    rows.forEach(function (r) { r.site = null; r.registry = null; byId[r.id] = r; });
    if (!m) return loose;

    (m.sites || []).forEach(function (site) {
      var id = m._eventIdOfSite ? m._eventIdOfSite(site) : null;
      if (id && byId[id] && !byId[id].site) byId[id].site = site;
      else loose.push({ kind: 'site', item: site, name: siteName(site) });
    });
    (m.registries || []).forEach(function (reg) {
      var id = m._eventIdOfRegistry ? m._eventIdOfRegistry(reg) : null;
      if (id && byId[id] && !byId[id].registry) byId[id].registry = reg;
      else loose.push({ kind: 'registry', item: reg, name: reg.title || 'Untitled registry' });
    });
    return loose;
  }

  function siteName(site) {
    return [site.partner1Name, site.partner2Name].filter(Boolean).join(' & ')
      || site.coupleNames || 'Untitled website';
  }

  // The builder reads ?id=. It is NOT ?siteId= — that opens a blank new site.
  function siteHref(id) { return '/wedding-websites/builder?id=' + esc(id); }

  /* A chip is glanceable, so the cents go. money() keeps them because the
     billing ledger is an amount someone reconciles; "$1,180" beside a couple's
     name is not. */
  function moneyWhole(amount, currency) {
    var n = Number(amount) || 0;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency', currency: String(currency || 'usd').toUpperCase(),
        maximumFractionDigits: 0
      }).format(n);
    } catch (e) {
      return '$' + Math.round(n);
    }
  }

  /* One chip per asset. Present ones link where the thing is managed and say
     the one fact worth knowing in a list — published or not, how much has been
     given. Absent ones are the offer to make it, and creating from here carries
     the event with it so the new thing arrives already paired. */
  function assetChip(kind, r) {
    var isSite = kind === 'site';
    var item = isSite ? r.site : r.registry;
    var word = isSite ? 'Website' : 'Registry';

    if (item) {
      // Live means there is somewhere public to send someone. A draft site's
      // /w/ URL is a 404, so it gets no Open button — and no QR without the
      // confirm downloadSiteQr already puts in the way.
      var live = isSite
        ? (item.status === 'published' || (item.settings && item.settings.isPublished === true))
        : item.status === 'active';
      // A draft registry says so, and does NOT report a $0 total that is zero
      // only because nobody can reach the page yet (task #649 round 4). Money
      // that was genuinely raised before it came down is still shown.
      var raised = moneyWhole((item.totalRaised || 0) / 100, item.currency);
      var meta;
      if (isSite) {
        meta = live ? 'Published' : 'Draft';
      } else if (live) {
        meta = raised;
      } else {
        meta = (item.totalRaised || 0) > 0 ? 'Draft \u00b7 ' + raised : 'Draft';
      }
      /* The two things a planner actually does with a client's site between
         now and the wedding: look at it, and get the QR that goes on the
         printed cards. Everything else lives one click away in the builder,
         which is what the chip body opens. Copy-link and co-owners are on the
         My Events row and deliberately not here — a studio row already carries
         nine buttons, and a planner shares a site by handing over the builder,
         not by pasting a URL from a list. */
      var extra = '';
      if (live) {
        extra += '<a class="pw-asset-act" target="_blank" rel="noopener" href="'
          + (isSite ? '/w/' + esc(item.slug || item.subdomain || '') : '/r/' + esc(item.id))
          + '" title="' + esc('Open the ' + word.toLowerCase()) + '"'
          + ' aria-label="' + esc('Open the ' + word.toLowerCase()) + '">'
          + '<i class="bi bi-box-arrow-up-right"></i></a>';

        // The QR is the one thing a planner takes OUT of this list — it goes on
        // the printed cards. A registry has exactly the same use for one, and
        // having it on the website chip and not the registry chip was the
        // inconsistency the host was looking at (task #649 round 2).
        //
        // It now sits INSIDE the live check with Open (round 4): a QR code is
        // the link, printed, and a printed code pointing at a 404 is worse
        // than a pasted one because you cannot take the cards back.
        extra += '<button type="button" class="pw-asset-act ' + (isSite ? 'js-siteqr' : 'js-registryqr') + '"'
          + ' data-' + (isSite ? 'site' : 'registry') + '="' + esc(item.id) + '"'
          + ' title="Download the QR code" aria-label="Download the QR code">'
          + '<i class="bi bi-qr-code"></i></button>';
      }

      return '<span class="pw-asset-wrap is-' + kind + '">'
        + '<a class="pw-asset is-' + kind + '" href="'
        + (isSite ? siteHref(item.id) : '/gift-registry?id=' + esc(item.id)) + '"'
        + ' title="' + esc(word + ' — ' + meta) + '">'
        + '<i class="bi ' + (isSite ? 'bi-heart-fill' : 'bi-gift-fill') + '"></i>'
        + '<b>' + word + '</b><small>' + esc(meta) + '</small></a>'
        + (extra ? '<span class="pw-asset-acts">' + extra + '</span>' : '')
        + '</span>';
    }
    // One line: the label says the whole thing. It used to be the word on one
    // line with "+ add" under it, which bought a second line to say what the
    // verb already says.
    return '<span class="pw-asset-wrap is-' + kind + ' is-add">'
      + '<a class="pw-asset is-' + kind + ' is-add" href="'
      + (isSite ? '/wedding-websites/builder?formId=' + esc(r.id)
                : '/gift-registry?new=1&formId=' + esc(r.id)) + '"'
      + ' title="' + esc('Add a ' + word.toLowerCase() + ' to ' + r.title) + '">'
      + '<i class="bi bi-plus-lg"></i>'
      + '<b>Add ' + word.toLowerCase() + '</b></a></span>';
  }

  function eventRowHtml(r) {
    var st = r.stats || {};
    var deadline = r.ev.deadlineInDays;
    var soon = deadline != null && deadline >= 0 && deadline <= 7;
    /* The exact date and the relative one are separate elements so the narrow
       layout can drop the exact one. "in 46 days" is the fact a planner works
       to; "2026-10-15" is the one they look up when they need it, and it stays
       in the tooltip and on the row's own dashboard. */
    var dateBits = r.ev.eventDate
      ? '<span class="pw-date" title="' + esc(r.ev.eventDate) + '">'
        + '<i class="bi bi-calendar3"></i> '
        + '<span class="pw-date-abs">' + esc(r.ev.eventDate)
          + (r.ev.eventInDays != null ? ' &middot; ' : '') + '</span>'
        + (r.ev.eventInDays != null
            ? '<span class="pw-date-rel">' + esc(relDays(r.ev.eventInDays)) + '</span>'
            : '')
        + '</span>'
      : '<span class="pw-date"><i class="bi bi-calendar3"></i> No date set</span>';

    // The RSVP deadline, not the wedding day, is the date a planner works to,
    // so it gets a chip of its own and goes red inside the last week.
    var overdue = deadline != null && deadline < 0;
    var badge = deadline != null
      ? '<span class="pw-due' + (soon ? ' is-soon' : '') + (overdue ? ' is-past' : '')
        + '"><i class="bi bi-clock"></i>RSVP ' + esc(relDays(deadline)) + '</span>'
      : '<span class="pw-due is-none" title="No RSVP deadline set on this event">No deadline</span>';
    var tier = r.signature
      ? '<span class="pw-tier is-signature"><i class="bi bi-star-fill"></i>Signature</span>'
      : (r.paid ? '<span class="pw-tier is-paid">Event Pro</span>'
                : '<span class="pw-tier">Free</span>');

    var guests = st.totalGuests != null ? st.totalGuests : (r.ev.guests || 0);
    var attending = st.attending != null ? st.attending : null;
    // Replied out of invited, because the missing half is the work. Falls back
    // to a bare count when there is no invited list to measure against.
    var invited = r.ev.invited != null ? r.ev.invited : null;
    var pct = (invited && invited > 0) ? Math.min(100, Math.round((r.responses / invited) * 100)) : null;

    var actions = ''
      + ico('Copy the guest link', '<i class="bi bi-link-45deg"></i>', 'js-copy')
      + ico('Download the QR code', '<i class="bi bi-qr-code"></i>', 'js-qr')
      + '<a class="btn-sm-icon" title="Invitations and reminders" aria-label="Invitations and reminders" href="'
        + esc(r.dashLink) + '&tab=campaigns"><i class="bi bi-send"></i></a>'
      + ico(r.notif ? 'Response notifications on' : 'Response notifications',
            '<i class="bi ' + (r.notif ? 'bi-bell-fill' : 'bi-bell') + '"></i>',
            'js-notif' + (r.notif ? ' is-active' : ''));

    if (!r.coOwned) {
      actions += ico('Duplicate', '<i class="bi bi-files"></i>', 'js-dup')
        + ico('Who can see this event', '<i class="bi bi-person-plus"></i>', 'js-share')
        + ico('Your branding on this event', '<i class="bi bi-palette"></i>', 'js-brand');
      if (r.paid) {
        actions += ico(r.isTemplate ? 'Saved as a template' : 'Save as a template',
          '<i class="bi ' + (r.isTemplate ? 'bi-bookmark-fill' : 'bi-bookmark-plus') + '"></i>',
          'js-tpl' + (r.isTemplate ? ' is-active' : ''));
      }
    }
    actions += '<a class="btn-sm-icon" title="Edit the form" aria-label="Edit the form" href="'
      + esc(r.editLink) + '"><i class="bi bi-pencil"></i></a>';
    if (!r.coOwned) actions += ico('Delete', '<i class="bi bi-x-lg"></i>', 'js-del');

    return '<article class="event-card pw-row" data-form="' + esc(r.id) + '"'
      + (r.client ? ' style="--brand:' + clientColor(r.client, 'ink') + '"' : '') + '>'
      + '<a class="event-card-main" href="' + esc(r.dashLink) + '">'
      +   '<div class="event-card-header"><div>'
      +     '<h6 class="event-card-title">' + esc(r.title) + '</h6>'
      +     '<div class="event-card-date">' + dateBits + badge + tier
      +       (r.coOwned ? '<span class="pw-tier">Shared with you</span>' : '')
      +     '</div>'
      +   '</div></div>'
      +   '<div class="event-card-stats">'
      +     '<div class="event-stat is-wide">'
      +       '<div class="event-stat-value">' + r.responses
      +         (invited ? '<small>/' + invited + '</small>' : '') + '</div>'
      +       '<div class="event-stat-label">Replied</div>'
      +       (pct == null ? ''
          : '<span class="pw-mini"><i style="width:' + pct + '%"></i></span>')
      +     '</div>'
      +     (attending != null
        ? '<div class="event-stat"><div class="event-stat-value" style="color:var(--org-green)">' + attending
          + '</div><div class="event-stat-label">Attending</div></div>'
        : '<div class="event-stat"><div class="event-stat-value" style="color:var(--org-gold-deep)">' + guests
          + '</div><div class="event-stat-label">Guests</div></div>')
      +   '</div>'
      + '</a>'
      + '<div class="event-card-actions">'
      +   '<span class="pw-assets">' + assetChip('site', r) + assetChip('registry', r) + '</span>'
      +   actions
      + '</div>'
      + '</article>';
  }

  // One delegated listener for every row action, on the list rather than on
  // each of ~200 buttons, and it looks the form up by id at click time so a
  // re-render never leaves a handler pointing at a detached row.
  function wireRowActions() {
    var list = $('pwEventList');
    if (!list || list.dataset.wired) return;
    list.dataset.wired = '1';
    list.addEventListener('click', function (e) {
      // The website QR chip is not a .btn-sm-icon, and its subject is the SITE
      // rather than the event, so it is answered before the row lookup below.
      var qr = e.target.closest('.js-siteqr');
      if (qr) {
        e.preventDefault();
        var m0 = mp();
        if (!m0) return;
        var sid = qr.getAttribute('data-site');
        var site = (m0.sites || []).filter(function (x) { return x.id === sid; })[0];
        if (!site) return;
        var slug = site.slug || site.subdomain || '';
        var isLive = site.status === 'published'
          || (site.settings && site.settings.isPublished === true);
        // downloadSiteQr does the not-live confirm itself, so the two entry
        // points cannot disagree about when a QR is safe to print.
        return m0.downloadSiteQr(sid, siteName(site),
          window.location.origin + '/w/' + slug, isLive);
      }

      // Same shape for the registry chip, through the registry's own handler
      // so the not-live confirm cannot disagree between the two entry points.
      var rqr = e.target.closest('.js-registryqr');
      if (rqr) {
        e.preventDefault();
        var m1 = mp();
        if (!m1) return;
        var rid = rqr.getAttribute('data-registry');
        var reg = (m1.registries || []).filter(function (x) { return x.id === rid; })[0];
        if (!reg) return;
        return m1.downloadRegistryQr(rid, reg.title || 'Registry',
          window.location.origin + '/r/' + rid, reg.status === 'active');
      }

      var btn = e.target.closest('.btn-sm-icon');
      if (!btn || btn.tagName === 'A') return;
      var row = btn.closest('[data-form]');
      if (!row) return;
      var id = row.getAttribute('data-form');
      var f = mpForms().filter(function (x) { return x.id === id; })[0];
      if (!f) return;
      var r = formMeta(f);
      var m = mp();
      if (!m) return;
      e.preventDefault();
      if (btn.classList.contains('js-copy')) return m.copyFormLink(id, r.publicUrl);
      if (btn.classList.contains('js-qr')) return m.downloadFormQr(id, r.title, r.publicUrl);
      if (btn.classList.contains('js-notif')) return m.openNotificationsModal(id, r.paid);
      if (btn.classList.contains('js-dup')) return m.duplicateForm(id);
      if (btn.classList.contains('js-share')) return m.openFormShareModal(id, r.title);
      if (btn.classList.contains('js-brand')) return m.openPlannerBranding(id, r.title);
      if (btn.classList.contains('js-tpl')) return m.openSaveTemplate(id, r.title);
      if (btn.classList.contains('js-del')) return m.confirmDeleteForm(id, r.title, r.paid);
    });
  }

  function mountFilters() {
    wireRowActions();
    var sel = $('pwFilterClient');
    if (sel) {
      var names = {};
      state.events.forEach(function (e) { if (e.client) names[e.client] = 1; });
      var opts = Object.keys(names).sort(function (a, b) { return a.localeCompare(b); });
      sel.innerHTML = '<option value="">All clients</option>'
        + opts.map(function (n) { return '<option value="' + esc(n) + '">' + esc(n) + '</option>'; }).join('')
        + '<option value="\u0000">Not filed under a client</option>';
      sel.value = filters.client;
    }
    if (mountFilters.wired) return;
    mountFilters.wired = true;
    [['pwSearch', 'q', 'input'], ['pwFilterClient', 'client', 'change'],
     ['pwFilterWhen', 'when', 'change'], ['pwFilterTier', 'tier', 'change'],
     ['pwGroup', 'group', 'change']].forEach(function (b) {
      var el = $(b[0]);
      if (!el) return;
      el.addEventListener(b[2], function () {
        filters[b[1]] = b[1] === 'q' ? String(this.value || '').toLowerCase().trim() : this.value;
        renderEvents();
      });
    });
  }

  function matches(r) {
    if (filters.q && r.title.toLowerCase().indexOf(filters.q) === -1) return false;
    if (filters.client) {
      if (filters.client === '\u0000') { if (r.client) return false; }
      else if (r.client !== filters.client) return false;
    }
    if (filters.tier === 'paid' && !r.paid) return false;
    if (filters.tier === 'free' && r.paid) return false;
    if (filters.when) {
      var d = r.ev.deadlineInDays == null ? r.ev.eventInDays : r.ev.deadlineInDays;
      if (filters.when === 'past' && !(d != null && d < 0)) return false;
      if (filters.when === 'upcoming' && !(d != null && d >= 0)) return false;
      if (filters.when === 'soon' && !(d != null && d >= 0 && d <= 30)) return false;
    }
    return true;
  }

  function renderEvents() {
    var host = $('pwEventList');
    if (!host) return;
    var all = mpForms().map(formMeta);
    var loose = pairAssets(all);
    var rows = all.filter(matches);

    renderSummary(all);

    var count = $('pwFilterCount');
    if (count) {
      count.textContent = rows.length === all.length
        ? all.length + (all.length === 1 ? ' event' : ' events')
        : rows.length + ' of ' + all.length;
    }

    if (!all.length) {
      host.innerHTML = emptyState('bi-calendar3', 'No events yet',
        'Every event you create is filed here, and you can group them by client.');
      return;
    }
    if (!rows.length) {
      host.innerHTML = emptyState('bi-funnel', 'Nothing matches',
        'Try a different client, date or plan.');
      return;
    }

    if (filters.group !== 'client') {
      host.innerHTML = '<div class="pw-rows">' + rows.map(eventRowHtml).join('') + '</div>'
        + looseGroupHtml(loose);
      return;
    }

    var groups = {};
    rows.forEach(function (r) { (groups[r.client] = groups[r.client] || []).push(r); });
    host.innerHTML = Object.keys(groups).sort(function (a, b) {
      if (!a) return 1;
      if (!b) return -1;
      return a.localeCompare(b);
    }).map(function (key) {
      var items = groups[key];
      var evs = items.map(function (r) { return r.ev; });
      var responses = items.reduce(function (n, r) { return n + (r.responses || 0); }, 0);
      var upcoming = evs.filter(function (e) { return e.eventInDays != null && e.eventInDays >= 0; }).length;
      return '<section class="pw-group">'
        + '<header class="pw-group-head">'
        + '<span class="pw-group-dot" style="background:' + clientColor(key || null, 'ink') + '"></span>'
        + '<h3>' + esc(key || 'Not filed under a client') + '</h3>'
        + '<span class="pw-group-meta">' + items.length + (items.length === 1 ? ' event' : ' events')
        + (upcoming ? ' &middot; ' + upcoming + ' upcoming' : '')
        + ' &middot; ' + responses + ' replies</span>'
        + '</header>'
        + '<div class="pw-rows">' + items.map(eventRowHtml).join('') + '</div>'
        + '</section>';
    }).join('') + looseGroupHtml(loose);
  }

  /* A website or registry with no event of its own.
   *
   * It is not an error and not a leftover — "Yael's Birthday" has no RSVP form
   * and never will. It gets its own group at the foot of the list rather than
   * being dropped into a client's, because sitting under a couple's name is
   * exactly the claim it cannot make.
   *
   * Only the free-text search applies to it. Client, date and plan are
   * questions about an event, so when one of those is set the group is hidden
   * rather than answered wrongly. */
  function looseGroupHtml(loose) {
    if (filters.client || filters.when || filters.tier) return '';
    var q = (filters.q || '').toLowerCase();
    var items = q ? loose.filter(function (x) { return x.name.toLowerCase().indexOf(q) !== -1; })
                  : loose;
    if (!items.length) return '';
    return '<section class="pw-group pw-group-loose">'
      + '<header class="pw-group-head">'
      + '<span class="pw-group-dot" style="background:#b9b2c4"></span>'
      + '<h3>On their own</h3>'
      + '<span class="pw-group-meta">' + items.length + ' not filed under an event</span>'
      + '</header>'
      + '<div class="pw-rows">' + items.map(function (x) {
          return x.kind === 'site' ? siteRowHtml(x.item) : registryRowHtml(x.item);
        }).join('') + '</div>'
      + '</section>';
  }

  // The strip above the list. Summed from the SAME rows drawn below it, so the
  // strip and the list can never disagree.
  function renderSummary(rows) {
    var strip = $('pwSummary');
    if (!strip) return;
    if (!rows.length) { strip.classList.add('d-none'); return; }
    var replies = 0, guests = 0, upcoming = 0;
    rows.forEach(function (r) {
      replies += r.responses || 0;
      guests += (r.stats && r.stats.totalGuests != null) ? r.stats.totalGuests : (r.ev.guests || 0);
      if (r.ev.eventInDays != null && r.ev.eventInDays >= 0) upcoming++;
    });
    strip.innerHTML = stat('icon-events', 'bi-calendar-event', rows.length, 'Events')
      + stat('icon-events', 'bi-hourglass-split', upcoming, 'Upcoming')
      + stat('icon-responses', 'bi-envelope-check', replies, 'Replies')
      + stat('icon-guests', 'bi-people', guests, 'Guests');
    strip.classList.remove('d-none');
  }

  function stat(iconCls, icon, value, label) {
    return '<div class="summary-stat"><span class="card-icon ' + iconCls + '"><i class="bi ' + icon
      + '"></i></span><span class="card-value">' + value + '</span><span class="card-label">'
      + esc(label) + '</span></div>';
  }

  function emptyState(icon, title, body) {
    return '<div class="empty-state"><div class="empty-state-icon"><i class="bi ' + icon + '"></i></div>'
      + '<h4>' + esc(title) + '</h4><p>' + esc(body) + '</p></div>';
  }

  /* ---------------------------------------------------------------- assets

     siteRowHtml and registryRowHtml no longer have tabs of their own to fill.
     They draw the rows in the "On their own" group, and they are tinted to
     their kind so a website is never mistaken for a client event in a list
     that is otherwise all client events.
     ------------------------------------------------------------------- */

  function siteRowHtml(site) {
    var m = mp();
    var published = site.status === 'published'
      || (site.settings && site.settings.isPublished === true);
    var names = [site.partner1Name, site.partner2Name].filter(Boolean).join(' & ')
      || site.coupleNames || 'Untitled website';
    var when = site.weddingDate && site.weddingDate._seconds
      ? new Date(site.weddingDate._seconds * 1000).toLocaleDateString(undefined,
          { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' })
      : 'No date set';
    var url = 'quikrsvp.com/w/' + (site.slug || '');

    // Task #785: say WHAT this row is. It used to open with the site's
    // template as a 10px swatch - which, for the default template's pale
    // grey, read as an unticked checkbox - and otherwise looked like an event
    // row. A globe where an event has nothing, and a "Website" chip in the
    // same style as the Free / Event Pro / Signature chips on event rows.
    var kindIcon = '<i class="bi bi-globe2 pw-kind-icon" aria-hidden="true"></i>';
    var kindChip = '<span class="pw-tier is-site"><i class="bi bi-globe2"></i>Website</span>';

    return '<article class="event-card pw-row is-plain pw-kind-site">'
      + '<a class="event-card-main" href="' + siteHref(site.id) + '">'
      +   '<div class="event-card-header"><div>'
      +     '<h6 class="event-card-title">' + kindIcon + esc(names) + '</h6>'
      +     '<div class="event-card-date">' + kindChip + '<i class="bi bi-calendar3"></i> ' + esc(when)
      +       '<span class="pw-url" dir="ltr">' + esc(url) + '</span>'
      +       (published ? '<span class="status-pill pill-yes"><i class="bi bi-check-circle"></i>Published</span>'
                         : '<span class="status-pill pill-pending"><i class="bi bi-pencil"></i>Draft</span>')
      +     '</div>'
      +   '</div></div>'
      + '</a>'
      + '<div class="event-card-actions">'
      +   (published ? '<a class="btn-sm-icon" title="View the site" href="/w/' + esc(site.slug || '')
          + '" target="_blank" rel="noopener"><i class="bi bi-box-arrow-up-right"></i></a>' : '')
      +   '<a class="btn-org-outline btn-sm" href="' + siteHref(site.id)
          + '"><i class="bi bi-pencil"></i>Edit</a>'
      + '</div>'
      + '</article>';
  }

  function registryRowHtml(reg) {
    var items = (reg.items || []).length;
    var funded = (reg.items || []).filter(function (i) { return i.isFunded; }).length;
    // totalRaised is in cents, the Stripe convention the rest of the product
    // follows; dividing here is what the My Events card does too.
    var raised = money((reg.totalRaised || 0) / 100, reg.currency);
    var active = reg.status === 'active';

    return '<article class="event-card pw-row is-plain pw-kind-registry">'
      + '<a class="event-card-main" href="/gift-registry?id=' + esc(reg.id) + '">'
      +   '<div class="event-card-header"><div>'
      +     '<h6 class="event-card-title"><i class="bi bi-gift pw-kind-icon" aria-hidden="true"></i>'
      +       esc(reg.title || 'Untitled registry') + '</h6>'
      +     '<div class="event-card-date"><span class="pw-tier is-registry"><i class="bi bi-gift"></i>Registry</span>'
      +       (active ? '<span class="status-pill pill-yes">Active</span>'
                      : '<span class="status-pill pill-pending">Draft</span>')
      +       (reg.description ? '<span class="pw-dim">' + esc(reg.description) + '</span>' : '')
      +     '</div>'
      +   '</div></div>'
      +   '<div class="event-card-stats">'
      +     '<div class="event-stat"><div class="event-stat-value" style="color:var(--org-green)">'
      +       esc(raised) + '</div><div class="event-stat-label">Raised</div></div>'
      +     '<div class="event-stat"><div class="event-stat-value">' + items
      +       '</div><div class="event-stat-label">Items</div></div>'
      +     '<div class="event-stat"><div class="event-stat-value" style="color:var(--org-gold-deep)">'
      +       funded + '</div><div class="event-stat-label">Funded</div></div>'
      +   '</div>'
      + '</a>'
      + '<div class="event-card-actions">'
      +   '<a class="btn-sm-icon" title="View the registry" target="_blank" rel="noopener" href="/r/'
      +     esc(reg.id) + '"><i class="bi bi-box-arrow-up-right"></i></a>'
      +   '<a class="btn-org-outline btn-sm" href="/gift-registry?id=' + esc(reg.id)
      +     '"><i class="bi bi-pencil"></i>Manage</a>'
      + '</div>'
      + '</article>';
  }

  /* =======================================================================
     Billing — the ledger, and the statement a planner hands to a client.

     The ledger my-projects.js draws answers "what am I being charged". The
     other half of a planner's month is the opposite question — what did THIS
     couple cost — and until now the only answer was a CSV they had to re-cut
     themselves. Picking a client here rebuilds the same charges as a
     statement under the studio's own name, and Print turns it into the PDF
     that goes out with their invoice.

     The subscription is deliberately absent from a client statement: it is
     the planner's cost of doing business, not the couple's, and putting it on
     a page a client reads would be a small lie.
     ======================================================================= */

  var billClient = '';

  function ledger() {
    return (window.myProjects && window.myProjects._pbLedger) || null;
  }

  // formId -> client, from the events the studio has already loaded. A charge
  // whose event is filed under a couple belongs on that couple's statement.
  function clientOfLine(line) {
    if (!line || line.isSubscription || !line.eventId) return '';
    for (var i = 0; i < state.events.length; i++) {
      if (state.events[i].id === line.eventId) return state.events[i].client || '';
    }
    return '';
  }

  function mountBillingBar() {
    var panel = $('pwBilling');
    if (!panel || panel.querySelector('.pw-bill-bar')) return;

    var names = {};
    state.events.forEach(function (e) { if (e.client) names[e.client] = 1; });
    var options = Object.keys(names).sort(function (a, b) { return a.localeCompare(b); });

    var bar = document.createElement('div');
    bar.className = 'pw-bill-bar';
    bar.innerHTML =
      '<label for="pwBillClient">Statement for</label>'
      + '<select id="pwBillClient"><option value="">Everything, by month</option>'
      + options.map(function (n) { return '<option value="' + esc(n) + '">' + esc(n) + '</option>'; }).join('')
      + '</select>'
      + '<button type="button" id="pwBillPrint" class="pw-bill-print">'
      + '<i class="bi bi-printer"></i> Print / save as PDF</button>';
    var head = panel.querySelector('.panel-header');
    panel.insertBefore(bar, head ? head.nextSibling : panel.firstChild);

    bar.querySelector('#pwBillClient').addEventListener('change', function () {
      billClient = this.value;
      renderBilling();
    });
    bar.querySelector('#pwBillPrint').addEventListener('click', printStatement);

    if (!options.length) {
      var sel = bar.querySelector('#pwBillClient');
      sel.disabled = true;
      sel.title = 'File an event under a client on the Clients tab and it becomes a statement here.';
    }
  }

  // Every charge for one client, in ledger order, with the month it fell in.
  function clientLines(name) {
    var data = ledger();
    if (!data || !Array.isArray(data.months)) return [];
    var out = [];
    data.months.forEach(function (m) {
      (m.lines || []).forEach(function (l) {
        if (clientOfLine(l) !== name) return;
        out.push({ line: l, month: m });
      });
    });
    return out;
  }

  function chargeLabel(l) {
    return (window.PlannerModals && window.PlannerModals.chargeLabel)
      ? window.PlannerModals.chargeLabel(l) : (l.label || '');
  }

  function renderBilling() {
    var body = document.getElementById('plannerBillingBody');
    if (!body) return;
    // "Everything, by month" is my-projects.js's own render. Leave it alone.
    if (!billClient) return;

    var rows = clientLines(billClient);
    if (!rows.length) {
      body.innerHTML = '<div class="pw-empty"><h3>Nothing charged to ' + esc(billClient) + ' yet</h3>'
        + '<p>Charges appear here as you upgrade their events.</p></div>';
      return;
    }
    body.innerHTML = statementHtml(billClient, rows);
  }

  function statementHtml(name, rows) {
    var currency = rows[0].line.currency;
    var mixed = false;
    var total = 0;
    rows.forEach(function (r) {
      total += (r.line.amount || 0);
      if (r.line.currency !== currency) mixed = true;
    });

    var body = rows.map(function (r) {
      var l = r.line;
      return '<tr>'
        + '<td class="d">' + esc(String(l.date).slice(0, 10)) + '</td>'
        + '<td>' + esc(l.eventTitle || name) + '</td>'
        + '<td class="c">' + esc(chargeLabel(l)) + '</td>'
        + '<td class="a' + (l.amount < 0 ? ' is-credit' : '') + '">'
        + esc(money(l.amount, l.currency)) + '</td>'
        + '</tr>';
    }).join('');

    return '<div class="pw-stmt">'
      + '<table class="pw-stmt-table"><thead><tr>'
      + '<th class="d">Date</th><th>Event</th><th class="c">Charge</th><th class="a">Amount</th>'
      + '</tr></thead><tbody>' + body + '</tbody></table>'
      + (mixed
        ? '<p class="pw-stmt-note">These charges are in more than one currency, so they are listed without a single total.</p>'
        : '<div class="pw-stmt-total"><span>Total for ' + esc(name) + '</span><b>'
          + esc(money(total, currency)) + '</b></div>')
      + '<p class="pw-stmt-note">Event charges only. The Event Planner subscription is billed to the '
      + 'studio and is not part of a client statement.</p>'
      + '</div>';
  }

  /**
   * Print the current view as a statement on the studio's own paper.
   *
   * A sheet built here and shown only to the printer, rather than a print
   * stylesheet laid over the live page: the live ledger is three nested
   * Bootstrap cards deep and prints as pages of chrome, and the thing a
   * planner staples to an invoice has to carry THEIR name at the top.
   */
  function printStatement() {
    var p = state.profile || {};
    var studio = (p.companyName || '').trim() || 'Your studio';
    var rows = billClient ? clientLines(billClient) : [];
    var sheet = document.getElementById('pwPrintSheet') || document.createElement('div');
    sheet.id = 'pwPrintSheet';

    var contact = [p.website, p.contactEmail, p.phone]
      .filter(Boolean).map(function (v) { return esc(v); }).join(' · ');
    var head = '<header class="pw-sheet-head">'
      + (p.logoUrl ? '<img src="' + esc(p.logoUrl) + '" alt="">' : '')
      + '<div><div class="pw-sheet-studio">' + esc(studio) + '</div>'
      + (contact ? '<div class="pw-sheet-contact">' + contact + '</div>' : '')
      + '</div></header>';

    var meta = '<div class="pw-sheet-meta"><span>'
      + esc(billClient ? 'Statement for ' + billClient : 'Account statement')
      + '</span><span>' + esc(ymd(new Date())) + '</span></div>';

    sheet.innerHTML = head + meta
      + (billClient && rows.length ? statementHtml(billClient, rows) : monthsSheet())
      + '<footer class="pw-sheet-foot">Prepared with QuikRSVP</footer>';
    if (!sheet.parentNode) document.body.appendChild(sheet);
    window.print();
  }

  // The whole ledger for the "everything" case. One table per month rather
  // than a card per month: it is going on paper.
  function monthsSheet() {
    var data = ledger();
    var months = (data && data.months) || [];
    if (!months.length) return '<p class="pw-stmt-note">No charges yet.</p>';
    return months.map(function (m) {
      var label = (window.PlannerModals && window.PlannerModals.monthLabel)
        ? window.PlannerModals.monthLabel(m) : m.label;
      var rows = (m.lines || []).map(function (l) {
        return '<tr><td class="d">' + esc(String(l.date).slice(0, 10)) + '</td>'
          + '<td>' + esc(l.isSubscription ? 'Your subscription' : (l.eventTitle || 'Account-wide')) + '</td>'
          + '<td class="c">' + esc(chargeLabel(l)) + '</td>'
          + '<td class="a' + (l.amount < 0 ? ' is-credit' : '') + '">'
          + esc(money(l.amount, l.currency)) + '</td></tr>';
      }).join('');
      return '<div class="pw-stmt"><h3 class="pw-stmt-h">' + esc(label) + '</h3>'
        + '<table class="pw-stmt-table"><tbody>' + rows + '</tbody></table>'
        + (m.mixedCurrency ? '' : '<div class="pw-stmt-total"><span>Total</span><b>'
            + esc(money(m.total, m.currency)) + '</b></div>')
        + '</div>';
    }).join('');
  }

  // Task #816: the Studio is the planner's command centre, so a link out of it
  // (an event's dashboard, the builder, a site, a registry, a preview) opens
  // in a new tab and the Studio stays where it was. One rule at the click
  // rather than target="_blank" on every anchor, so links added later - and
  // the event cards my-projects.js draws in here - are covered too. Links
  // within the Studio itself (#hash, /planner) and downloads stay put.
  function opensOutOfStudio(a) {
    if (!a || a.hasAttribute('download') || (a.target && a.target !== '_self')) return false;
    var raw = a.getAttribute('href') || '';
    if (!raw || raw.charAt(0) === '#' || /^(javascript|mailto|tel):/i.test(raw)) return false;
    var u;
    try { u = new URL(a.href, window.location.href); } catch (e) { return false; }
    if (!/^https?:$/.test(u.protocol)) return false;
    return !/^\/(?:(?:es|fr|de|pt|he|ar)\/)?planner(\.html)?\/?$/.test(u.pathname);
  }

  function wire() {
    document.addEventListener('click', function (ev) {
      var outLink = ev.target.closest('#pwBody a[href]');
      if (outLink && ev.button === 0 && !ev.defaultPrevented && opensOutOfStudio(outLink)) {
        outLink.target = '_blank';
        outLink.rel = 'noopener';
      }
      var viewBtn = ev.target.closest('[data-view]');
      if (viewBtn) return switchView(viewBtn.getAttribute('data-view'));

      var nav = ev.target.closest('[data-month]');
      if (nav) {
        var step = Number(nav.getAttribute('data-month'));
        state.month = step === 0
          ? new Date(new Date().getFullYear(), new Date().getMonth(), 1)
          : new Date(state.month.getFullYear(), state.month.getMonth() + step, 1);
        return renderSeason();
      }

      if (ev.target.closest('[data-task-new]')) { ev.preventDefault(); return openTaskModal(); }
      var chaseX = ev.target.closest('[data-chase-dismiss]');
      if (chaseX) { ev.preventDefault(); return setChaseDismissed(chaseX.getAttribute('data-chase-dismiss'), true); }
      var chaseBack = ev.target.closest('[data-chase-restore]');
      if (chaseBack) { ev.preventDefault(); return setChaseDismissed(chaseBack.getAttribute('data-chase-restore'), false); }

      var del = ev.target.closest('[data-del]');
      if (del) { ev.preventDefault(); return removeNote(del.getAttribute('data-del')); }

      var add = ev.target.closest('[data-add]');
      if (add) { ev.preventDefault(); return promptNote(add.getAttribute('data-add'), add); }

      var assign = ev.target.closest('[data-assign]');
      if (assign) { ev.preventDefault(); return promptClient(assign.getAttribute('data-assign'), assign); }

      // Task #696: tasks and alert timing.
      var toggle = ev.target.closest('[data-task-toggle]');
      if (toggle) {
        ev.preventDefault();
        var t = taskById(toggle.getAttribute('data-task-toggle'));
        return t && setTaskStatus(t.id, t.status === 'done' ? 'open' : 'done');
      }
      var editBtn = ev.target.closest('[data-task-edit]');
      if (editBtn) { ev.preventDefault(); var et = taskById(editBtn.getAttribute('data-task-edit')); return et && openTaskModal(et); }
      var del = ev.target.closest('[data-task-delete]');
      if (del) {
        ev.preventDefault();
        if (!window.confirm('Delete this task? This cannot be undone.')) return;
        var dm = $('pwTaskModal');
        if (dm && window.bootstrap) window.bootstrap.Modal.getOrCreateInstance(dm).hide();
        return setTaskStatus(del.getAttribute('data-task-delete'), 'dismissed');
      }
      // A click anywhere else closes the floating Alert timing card (#806).
      var openPanel = document.getElementById('pwAlertSettings');
      if (openPanel && !openPanel.classList.contains('d-none')
          && !openPanel.contains(ev.target) && !ev.target.closest('[data-alerts-toggle]')) {
        openPanel.classList.add('d-none');
        var tb = document.querySelector('[data-alerts-toggle]');
        if (tb) tb.setAttribute('aria-expanded', 'false');
      }
      var foldBtn = ev.target.closest('[data-ny-fold]');
      if (foldBtn) { ev.preventDefault(); return toggleNyFold(foldBtn); }
      var alertsBtn = ev.target.closest('[data-alerts-toggle]');
      if (alertsBtn) {
        var panel = $('pwAlertSettings');
        var opening = panel.classList.contains('d-none');
        panel.classList.toggle('d-none', !opening);
        alertsBtn.setAttribute('aria-expanded', opening ? 'true' : 'false');
        return;
      }
      if (ev.target.closest('[data-alerts-save]')) return saveAlertDays();
      var evRow = ev.target.closest('[data-alert-ev]');
      if (evRow && ev.target.closest('[data-alert-ev-save]')) {
        var v = evRow.querySelector('input').value.trim();
        return saveEventAlert(evRow.getAttribute('data-alert-ev'), v || null);
      }
      if (evRow && ev.target.closest('[data-alert-ev-clear]')) return saveEventAlert(evRow.getAttribute('data-alert-ev'), null);
    });

    document.addEventListener('change', function (ev) {
      if (ev.target && ev.target.id === 'pwAlertEmails') saveAlertEmails(ev.target.checked);
    });
    document.addEventListener('submit', function (ev) {
      if (ev.target && ev.target.id === 'pwTaskAdd') { ev.preventDefault(); addTaskFromForm(ev.target); }
    });
    document.addEventListener('keydown', function (ev) {
      if (ev.key === 'Escape') {
        var p = document.getElementById('pwAlertSettings');
        if (p && !p.classList.contains('d-none')) {
          p.classList.add('d-none');
          var tb = document.querySelector('[data-alerts-toggle]');
          if (tb) { tb.setAttribute('aria-expanded', 'false'); tb.focus(); }
        }
      }
      if (ev.key === 'Enter' && ev.target && ev.target.id === 'pwAlertDays') { ev.preventDefault(); saveAlertDays(); }
    });
  }

  /* =======================================================================
     Inline editor.

     One popover at a time, anchored to whatever was clicked. Enter saves,
     Escape cancels, clicking away cancels, and focus moves into the field and
     back to the anchor afterwards — the things a prompt() gave us for free and
     that have to be put back by hand.
     ======================================================================= */

  var openEditor = null;

  function closeEditor(restoreFocus) {
    if (!openEditor) return;
    var anchor = openEditor.anchor;
    openEditor.el.remove();
    document.removeEventListener('keydown', openEditor.onKey, true);
    document.removeEventListener('mousedown', openEditor.onDown, true);
    window.removeEventListener('scroll', openEditor.onScroll, true);
    window.removeEventListener('resize', openEditor.onScroll);
    openEditor = null;
    if (restoreFocus && anchor && anchor.focus) { try { anchor.focus(); } catch (e) {} }
  }

  /**
   * @param {Element} anchor
   * @param {object} opts
   *   title       heading above the field
   *   value       starting value
   *   placeholder
   *   hint        one line under the field
   *   options     [] offered as a datalist — the existing client names
   *   kinds       [{value,label}] rendered as a chip row
   *   kind        the chip selected on open
   *   clearLabel  when present, a third button that saves an empty value
   *   onSave(value, kind)
   */
  function inlineEdit(anchor, opts) {
    closeEditor(false);
    var o = opts || {};
    var listId = 'pw-list-' + Date.now();

    var el = document.createElement('div');
    el.className = 'pw-pop';
    el.setAttribute('role', 'dialog');
    el.setAttribute('aria-label', o.title || 'Edit');
    el.innerHTML =
      '<div class="pw-pop-title">' + esc(o.title || '') + '</div>'
      + '<input class="pw-pop-input" type="text" value="' + esc(o.value || '') + '" '
      + 'placeholder="' + esc(o.placeholder || '') + '" maxlength="120"'
      + (o.options && o.options.length ? ' list="' + listId + '"' : '') + '>'
      + (o.options && o.options.length
        ? '<datalist id="' + listId + '">'
          + o.options.map(function (v) { return '<option value="' + esc(v) + '">'; }).join('')
          + '</datalist>'
        : '')
      + (o.kinds
        ? '<div class="pw-pop-kinds">' + o.kinds.map(function (k) {
            return '<button type="button" class="pw-pop-kind' + (k.value === o.kind ? ' is-on' : '')
              + '" data-kind="' + esc(k.value) + '">' + esc(k.label) + '</button>';
          }).join('') + '</div>'
        : '')
      + (o.hint ? '<div class="pw-pop-hint">' + esc(o.hint) + '</div>' : '')
      + '<div class="pw-pop-actions">'
      + (o.clearLabel ? '<button type="button" class="pw-pop-clear">' + esc(o.clearLabel) + '</button>' : '')
      + '<button type="button" class="pw-pop-cancel">Cancel</button>'
      + '<button type="button" class="pw-pop-save">Save</button>'
      + '</div>';

    document.body.appendChild(el);

    // Anchored below, then pulled back inside the viewport rather than being
    // allowed to hang off the right edge on a narrow screen.
    var r = anchor.getBoundingClientRect();
    var w = el.offsetWidth;
    var left = Math.min(Math.max(8, r.left + window.scrollX), window.scrollX + window.innerWidth - w - 8);
    var top = r.bottom + window.scrollY + 6;
    if (r.bottom + el.offsetHeight + 12 > window.innerHeight) {
      top = Math.max(window.scrollY + 8, r.top + window.scrollY - el.offsetHeight - 6);
    }
    el.style.left = left + 'px';
    el.style.top = top + 'px';

    var input = el.querySelector('.pw-pop-input');
    var kind = o.kind;

    function commit(value) {
      var v = value == null ? input.value : value;
      closeEditor(true);
      o.onSave(v, kind);
    }

    el.addEventListener('click', function (ev) {
      var k = ev.target.closest('[data-kind]');
      if (k) {
        kind = k.getAttribute('data-kind');
        el.querySelectorAll('.pw-pop-kind').forEach(function (b) {
          b.classList.toggle('is-on', b === k);
        });
        input.focus();
        return;
      }
      if (ev.target.closest('.pw-pop-save')) return commit();
      if (ev.target.closest('.pw-pop-clear')) return commit('');
      if (ev.target.closest('.pw-pop-cancel')) return closeEditor(true);
    });

    var onKey = function (ev) {
      if (ev.key === 'Escape') { ev.stopPropagation(); closeEditor(true); }
      else if (ev.key === 'Enter' && el.contains(ev.target)) { ev.preventDefault(); commit(); }
    };
    var onDown = function (ev) { if (!el.contains(ev.target)) closeEditor(false); };
    var onScroll = function () { closeEditor(false); };

    document.addEventListener('keydown', onKey, true);
    document.addEventListener('mousedown', onDown, true);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);

    openEditor = { el: el, anchor: anchor, onKey: onKey, onDown: onDown, onScroll: onScroll };
    input.focus();
    input.select();
  }

  /** Every client name already in use, so the same couple is spelled once. */
  function knownClients() {
    var seen = {};
    state.events.forEach(function (e) { if (e.client) seen[e.client] = 1; });
    return Object.keys(seen).sort();
  }

  function promptNote(date, anchor) {
    inlineEdit(anchor, {
      title: 'Add to ' + date,
      placeholder: 'Cake tasting, site visit, away…',
      hint: 'Yours only. Guests and clients never see your diary.',
      kinds: [
        { value: 'note', label: 'Note' },
        { value: 'meeting', label: 'Meeting' },
        { value: 'blocked', label: 'Unavailable' }
      ],
      kind: 'note',
      onSave: function (title, kind) {
        if (!String(title || '').trim()) return;
        api('/api/planner/notes', {
          method: 'POST',
          body: JSON.stringify({ date: date, title: title, kind: kind || 'note' })
        }).then(function (res) {
          state.notes.push(res.note);
          renderSeason();
        }).catch(function (e) { toast(e.message, 'error'); });
      }
    });
  }

  function promptClient(formId, anchor) {
    var e = state.events.filter(function (x) { return x.id === formId; })[0];
    if (!e) return;
    inlineEdit(anchor, {
      title: 'Client for “' + e.title + '”',
      value: e.client || '',
      placeholder: 'Sarah & David',
      options: knownClients(),
      hint: 'Use the same name for every event you run for them — that is what groups them.',
      clearLabel: e.client ? 'Unfile' : '',
      onSave: function (name) {
        api('/api/planner/clients/' + encodeURIComponent(formId), {
          method: 'PUT', body: JSON.stringify({ client: name })
        }).then(function (res) {
          e.client = res.client;
          renderAll();
        }).catch(function (err) { toast(err.message, 'error'); });
      }
    });
  }

  function renderAll() {
    // A render replaces the element the popover is anchored to, so anything
    // still open would be left pointing at a detached node.
    closeEditor(false);
    renderSeason();
    renderAttention();
    // Filing an event under a client regroups the Events list, so it is redrawn
    // from the studio's own data whenever the workspace reloads.
    if (loadedTools.events) { mountFilters(); renderEvents(); }
  }

  /* =======================================================================
     Boot
     ======================================================================= */

  function gate() {
    $('pwLoading').classList.add('d-none');
    $('pwNotPlanner').classList.remove('d-none');
  }

  // The nav lives in the sidebar rather than in #pwBody, so it has to be
  // revealed with it — a non-planner must not see nine sections over a gate.
  function revealStudio() {
    $('pwLoading').classList.add('d-none');
    $('pwBody').classList.remove('d-none');
    var tabs = $('pwTabs');
    if (tabs) tabs.classList.remove('d-none');
    adoptTools();
    // The planner group in the account menu — Studio, Branding, Billing and
    // Subscription — is revealed by my-projects.js once it has confirmed the
    // subscription, and it only does that at the end of loadForms(). Without
    // this a planner who never opened the Events tab had no "Manage
    // subscription" in the one menu it lives in.
    if (mp() && mp()._revealPlannerBranding) mp()._revealPlannerBranding();
    openViewFromHash();
  }

  // /planner#billing lands on the ledger. The account menu links here, and so
  // does anything that used to open the billing dialog on another page.
  function openViewFromHash() {
    var hash = (window.location.hash || '').replace('#', '');
    if (hash && VIEWS.indexOf(hash) !== -1) switchView(hash);
  }

  function justSubscribed() {
    return new URLSearchParams(window.location.search).get('planner_subscribed') === '1';
  }

  /**
   * Poll until the subscription webhook lands, then draw the studio. Capped so
   * a genuinely failed payment still reaches the gate rather than spinning for
   * ever — 20 tries at 1.5s is 30 seconds, well beyond a normal webhook.
   */
  function waitForActivation(attempt) {
    attempt = attempt || 0;
    if (attempt > 20) return gate();
    setTimeout(function () {
      window.PlannerSubscription.getStatus(true).then(function (s) {
        if (!(s && s.isActive)) return waitForActivation(attempt + 1);
        state.isPlanner = true;
        renderStudio();
        load().then(function () {
          revealStudio();
          renderAll();
        });
      }).catch(function () { waitForActivation(attempt + 1); });
    }, 1500);
  }

  function start() {
    state.month = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
    wire();

    var status = window.PlannerSubscription && window.PlannerSubscription.getStatus
      ? window.PlannerSubscription.getStatus()
      : Promise.resolve(null);

    status.then(function (s) {
      state.isPlanner = !!(s && s.isActive);
      // Someone arriving straight from checkout can beat their own webhook
      // here. Greeting the person who has just paid with "this is part of
      // Event Planner" would be the worst thirty seconds of the product, so
      // that one case waits and asks again instead of gating.
      if (!state.isPlanner && justSubscribed()) return waitForActivation();
      if (!state.isPlanner) return gate();
      renderStudio();
      return load().then(function () {
        revealStudio();
        renderAll();
      });
    }).catch(function () {
      // A status call that fails is not evidence the visitor is not a planner,
      // but it IS a page that cannot draw. Say so instead of spinning forever.
      $('pwLoading').classList.add('d-none');
      $('pwNotPlanner').classList.remove('d-none');
    });
  }

  window.plannerWorkspace = {
    reload: function () { return load().then(renderAll); },
    open: switchView
  };

  window.addEventListener('hashchange', openViewFromHash);

  // Firebase auth bootstraps asynchronously; wait for it the same way
  // my-projects.js does rather than racing it.
  function whenAuthReady(attempt) {
    attempt = attempt || 0;
    if (window.firebaseAuth) {
      window.firebaseAuth.onAuthStateChanged(function (user) {
        if (!user) { window.location.href = '/my-projects'; return; }
        start();
      });
      return;
    }
    if (attempt > 100) return gate();
    setTimeout(function () { whenAuthReady(attempt + 1); }, 100);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { whenAuthReady(0); });
  } else {
    whenAuthReady(0);
  }
})();
